import React, { useState, useEffect } from 'react';
import { isUndefined } from 'lodash';
import { Drawer, DrawerTrigger, DrawerContent, DrawerHeader } from '@fiverr-private/overlay';
import { Button } from '@fiverr-private/button';
import { Container } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { getContext } from '@fiverr-private/fiverr_context';
import { ExtraWaveStatusInfo } from '../BriefConfirmationScreen';
import { BriefClassificationResponse } from '../../api';
import { Currency } from '../../types/currency';
import { BriefCreationContextProvider, useBriefCreationContext } from '../../context/BriefCreationContext';
import { Step } from '../../constants';
import { EditStep } from '../EditStep';
import { BriefCreation } from '../BriefCreation';
import { AppI18n, FormattedTranslationKey } from '../../i18n';
import { usePopulateBuyerTypeForCreate } from '../../hooks/classification/usePopulateBuyerTypeForCreate';
import { defaultCurrency } from '../../context/BriefCreationContext.constants';
import EditBriefCloseButton from './EditBriefCloseButton';

export interface EditBriefDrawerProps {
  briefId?: string;
  brief: any;
  currency?: Currency;
  briefClassification?: BriefClassificationResponse;
  extraWaveStatus?: ExtraWaveStatusInfo;
  triggerLabel?: string;
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  headerKey?: FormattedTranslationKey;
  enableScrollableHeader?: boolean;
}

const EditBriefDrawerContent = ({
  briefId,
  extraWaveStatus,
  onSuccess,
  onError,
  headerKey,
  handleClose,
  onStepChange,
  enableScrollableHeader,
}: {
  briefId?: string;
  extraWaveStatus?: ExtraWaveStatusInfo;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  headerKey: FormattedTranslationKey;
  handleClose: () => void;
  onStepChange?: (step: Step) => void;
  enableScrollableHeader?: boolean;
}) => {
  const { currentStep, setIsOpen } = useBriefCreationContext();

  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  const isCreateMode = !briefId;
  const stepsToHideHeader = [Step.Finish, Step.ProMigration];

  usePopulateBuyerTypeForCreate({ isCreateMode });

  useEffect(() => {
    if (currentStep === Step.Finish && isCreateMode) {
      setIsOpen(true);
    }
  }, [currentStep, isCreateMode, setIsOpen]);

  if (isCreateMode) {
    const shouldShowDrawerHeader =
      !stepsToHideHeader.includes(currentStep) && !(enableScrollableHeader && currentStep === Step.Edit);

    return (
      <>
        {shouldShowDrawerHeader && (
          <DrawerHeader>
            <Text size="b_lg" fontWeight="bold">
              <AppI18n k={headerKey} />
            </Text>
            <EditBriefCloseButton onClose={handleClose} briefId={briefId} />
          </DrawerHeader>
        )}
        <Container flex={1} overflow="hidden" background="grey_100" padding="0" position="relative">
          <BriefCreation />
        </Container>
      </>
    );
  }

  return (
    <>
      {!enableScrollableHeader && (
        <DrawerHeader>
          <Text size="b_lg" fontWeight="bold">
            <AppI18n k={headerKey} />
          </Text>
          <EditBriefCloseButton onClose={handleClose} briefId={briefId} />
        </DrawerHeader>
      )}

      <Container flex={1} overflow="hidden" background="grey_100" padding="0" position="relative">
        <EditStep
          briefId={briefId}
          extraWaveStatus={extraWaveStatus}
          onSuccess={onSuccess}
          onError={onError}
          fromEditSession
          enableScrollableHeader={enableScrollableHeader}
        />
      </Container>
    </>
  );
};

export const EditBriefDrawer = ({
  briefId,
  brief,
  currency = defaultCurrency,
  briefClassification,
  extraWaveStatus,
  triggerLabel = 'Open Edit Drawer',
  isOpen: controlledIsOpen,
  onClose: controlledOnClose,
  onSuccess,
  onError,
  headerKey = 'edit_drawer_header',
  enableScrollableHeader,
}: EditBriefDrawerProps) => {
  const { isMobile } = getContext();
  const shouldEnableScrollableHeader = enableScrollableHeader ?? isMobile;
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [currentStepForSize, setCurrentStepForSize] = useState<Step | null>(null);

  const isControlled = !isUndefined(controlledIsOpen) && !!controlledOnClose;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const handleClose = isControlled ? controlledOnClose : () => setInternalIsOpen(false);
  const handleOpen = () => setInternalIsOpen(true);

  const handleSetIsOpen = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  const isCreateMode = !briefId;
  const drawerSize = isCreateMode && currentStepForSize === Step.Finish ? 'md' : 'xl';

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      {!isControlled && (
        <DrawerTrigger>
          <Button onClick={handleOpen}>{triggerLabel}</Button>
        </DrawerTrigger>
      )}

      <DrawerContent size={drawerSize}>
        <BriefCreationContextProvider
          initialStep={Step.Edit}
          brief={brief}
          currency={currency}
          briefClassification={briefClassification}
          isOpen={isOpen}
          setIsOpen={handleSetIsOpen}
          rollouts={{ inMustangsPersonBrief: true }}
          enableEnhancedUx
          onSuccess={onSuccess}
        >
          <EditBriefDrawerContent
            briefId={briefId}
            extraWaveStatus={extraWaveStatus}
            onSuccess={onSuccess}
            onError={onError}
            headerKey={headerKey}
            handleClose={handleClose}
            onStepChange={setCurrentStepForSize}
            enableScrollableHeader={shouldEnableScrollableHeader}
          />
        </BriefCreationContextProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default EditBriefDrawer;
